import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, OnChanges, OnDestroy, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AppApis } from '@app/core/APIs/app-apis';
import { WorkflowDefinitionDocument, WorkflowDefinitionDraftDetail, requireWorkflowPayloadVersion } from './platform-workflow-definition-lifecycle.models';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';

interface Policy {
  id: string;
  code: string;
  revision: number;
  counterId: string;
  /** Existing catalog metadata; it is never copied into the draft declaration. */
  parts?: unknown;
}
interface PolicyContext { agency: number | null; program: number | null; }

@Component({
  selector: 'phms-workflow-numbering-settings',
  imports: [UiButtonDirective, UiControlDirective, CommonModule, FormsModule],
  templateUrl: './workflow-numbering-settings.component.html',
  styleUrl: './workflow-numbering-settings.component.scss',
})
export class WorkflowNumberingSettingsComponent implements OnChanges, OnDestroy {
  @Input() document: WorkflowDefinitionDocument | null = null;
  @Input() disabled = false;
  @Input() interactionBlocked = false;
  @Input() issuingLocation = '';
  @Input() latestDocument: (() => WorkflowDefinitionDocument | null) | null = null;
  @Input() savedDraft: Pick<WorkflowDefinitionDraftDetail, 'id' | 'eTag' | 'document'> | null = null;
  @Output() openGeneralRequested = new EventEmitter<void>();
  @Output() documentChange = new EventEmitter<WorkflowDefinitionDocument>();
  readonly policies = signal<Policy[]>([]);
  readonly loading = signal(false);
  readonly error = signal('');
  readonly catalogLoaded = signal(false);
  readonly changeError = signal('');
  readonly configuring = signal(false);
  private readonly http = inject(HttpClient);
  private readonly apis = inject(AppApis);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true });
  private scope = '';
  private generation = 0;
  private destroyed = false;

  get compatible(): boolean {
    return numberingFormatSupported(this.document);
  }
  get mode(): string { return this.document?.numbering?.mode ?? 'disabled'; }
  get hearing(): boolean { return this.document?.subjectTypeCode === 'hearing_cycle'; }
  get savedIssuingLocation(): boolean {
    const saved = this.savedDraft?.document, current = this.document;
    return !!saved && !!current && saved.subjectTypeCode === current.subjectTypeCode &&
      saved.workflowCode === current.workflowCode && saved.scopeTypeCode === current.scopeTypeCode && saved.scopeKey === current.scopeKey;
  }
  get declarationSupported(): boolean {
    const numbering: unknown = this.document?.numbering;
    if (numbering == null) return true;
    if (typeof numbering !== 'object' || Array.isArray(numbering)) return false;
    const value = numbering as Record<string, unknown>;
    return Object.keys(value).every(key => ['mode', 'policyId', 'agencyCode', 'programCode'].includes(key)) &&
      typeof value['mode'] === 'string' && ['disabled', 'required'].includes(value['mode']) &&
      ['policyId', 'agencyCode', 'programCode'].every(key => value[key] == null || typeof value[key] === 'string');
  }
  get authoringDisabled(): boolean {
    return this.disabled || this.interactionBlocked || this.configuring() || !this.compatible || !this.declarationSupported;
  }
  get selectedPolicyId(): string { return this.document?.numbering?.policyId ?? ''; }
  get selectedPolicy(): Policy | null {
    const matches = this.policies().filter(policy => policy.id === this.selectedPolicyId);
    return matches.length === 1 ? matches[0] : null;
  }
  get hasRetainedPolicyOption(): boolean { return !!this.selectedPolicyId && !this.selectedPolicy; }
  get unavailablePolicy(): boolean {
    return this.hasRetainedPolicyOption && this.catalogLoaded() && !this.loading() && !this.error();
  }
  get retainedPolicyLabel(): string {
    return `Saved policy ${this.selectedPolicyId}${this.unavailablePolicy ? ' (unavailable)' : ' (availability not confirmed)'}`;
  }
  get issuingScopeSupported(): boolean {
    const type = this.document?.scopeTypeCode, key = this.document?.scopeKey;
    return typeof type === 'string' && type !== 'global' && type.length <= 80 &&
      typeof key === 'string' && key.length <= 160 &&
      /^[a-z0-9._:/-]+$/.test(type) && /^[a-z0-9._:/-]+$/.test(key);
  }
  get issuingLocationLabel(): string {
    if (this.issuingLocation) return this.issuingLocation;
    const type = this.document?.scopeTypeCode;
    const label = type === 'global' ? 'All locations' : type === 'jurisdiction' ? 'Jurisdiction'
      : type === 'program' ? 'Program' : type === 'jurisdiction-program' ? 'Jurisdiction and program' : type || 'Not configured';
    return type === 'global' ? label : `${label}${this.document?.scopeKey ? ' · ' + this.document.scopeKey : ' · choose a location'}`;
  }
  get policyContext(): PolicyContext | null { return readPolicyContext(this.selectedPolicy?.parts); }
  get agencyCodeLimit(): number | null { return this.policyContext?.agency ?? null; }
  get programCodeLimit(): number | null { return this.policyContext?.program ?? null; }
  get contextMetadataUnavailable(): boolean { return this.selectedPolicy !== null && this.policyContext === null; }
  get retainedAgencyCode(): string | null {
    return this.agencyCodeLimit === null ? this.document?.numbering?.agencyCode ?? null : null;
  }
  get retainedProgramCode(): string | null {
    return this.programCodeLimit === null ? this.document?.numbering?.programCode ?? null : null;
  }

  ngOnChanges(): void {
    const scope = this.catalogContext();
    if (scope !== this.scope) {
      this.scope = scope;
      void this.load();
    }
  }
  ngOnDestroy(): void { this.destroyed = true; this.generation++; }

  openGeneral(): void {
    if (!this.interactionBlocked) this.openGeneralRequested.emit();
  }

  async setUpHearingNumbering(): Promise<void> {
    const draft = this.savedDraft, context = this.catalogContext();
    if (!draft || !this.hearing || !this.savedIssuingLocation || !this.issuingScopeSupported || this.authoringDisabled) return;
    this.changeError.set('');
    this.configuring.set(true);
    try {
      const { policyId } = await firstValueFrom(this.http.post<{ policyId: string }>(
        this.apis.core.manual.platformWorkflowExecution() + `/definition-drafts/${draft.id}/hearing-numbering`, null,
        { headers: { 'If-Match': draft.eTag, 'Idempotency-Key': crypto.randomUUID(), 'skip-error-interceptor': 'yes' }, withCredentials: true },
      ));
      if (this.destroyed || context !== this.catalogContext() || draft.id !== this.savedDraft?.id || draft.eTag !== this.savedDraft.eTag) return;
      this.configuring.set(false);
      this.change({ mode: 'required', policyId });
      await this.load();
    } catch (error) {
      if (!this.destroyed && context === this.catalogContext()) {
        this.changeError.set(error instanceof HttpErrorResponse && error.status === 403
          ? 'Setting up numbering requires workflow activation access for this issuing location. Creating a new policy also requires publication access.'
          : error instanceof HttpErrorResponse && error.status === 412
            ? 'The saved draft changed. Refresh it before setting up numbering again.'
            : 'Hearing numbering could not be set up. Try again.');
      }
    } finally {
      this.configuring.set(false);
    }
  }

  change(patch: Partial<NonNullable<WorkflowDefinitionDocument['numbering']>>): void {
    if (this.authoringDisabled || !this.document) return;
    const latest = this.latestDocument ? this.latestDocument() : this.document;
    if (!latest || !numberingFormatSupported(latest) ||
      JSON.stringify([latest.workflowCode, latest.definitionVersionCode, latest.packReference, latest.scopeTypeCode, latest.scopeKey]) !==
        JSON.stringify([this.document.workflowCode, this.document.definitionVersionCode, this.document.packReference, this.document.scopeTypeCode, this.document.scopeKey]) ||
      JSON.stringify(latest.numbering ?? null) !== JSON.stringify(this.document.numbering ?? null)) {
      this.changeError.set('The numbering selection or draft context changed. Review the current settings before editing them.');
      return;
    }
    this.changeError.set('');
    let numbering = { mode: 'disabled' as const, ...latest.numbering, ...patch };
    if (numbering.mode === 'disabled') numbering = { mode: 'disabled' };
    this.documentChange.emit({
      ...latest,
      payloadSchemaVersion: requireWorkflowPayloadVersion(latest.payloadSchemaVersion, '1.6'),
      triggers: latest.triggers ?? [],
      numbering,
    });
  }

  focusConfiguration(path = '$.numbering'): void {
    const context = this.catalogContext();
    setTimeout(() => {
      if (this.destroyed || this.ownerViews?.focusSuspended() || context !== this.catalogContext() ||
        this.element.nativeElement.closest('[hidden], [inert]')) return;
      const host = this.element.nativeElement;
      const field = path.includes('policyId') ? host.querySelector<HTMLElement>('select:not(:disabled)')
        : path.includes('agencyCode') ? host.querySelector<HTMLElement>('input[aria-label="Agency code"]:not(:disabled)')
          : path.includes('programCode') ? host.querySelector<HTMLElement>('input[aria-label="Program code"]:not(:disabled)') : null;
      (field ?? host.querySelector<HTMLElement>('input[type="radio"]:checked:not(:disabled)') ??
        host.querySelector<HTMLElement>('[tabindex="-1"]'))?.focus();
    });
  }

  private catalogContext(): string {
    return JSON.stringify([this.document?.workflowCode, this.document?.scopeTypeCode,
      this.document?.scopeKey, this.compatible]);
  }

  async load(): Promise<void> {
    if (this.destroyed) return;
    const generation = ++this.generation, context = this.catalogContext(), document = this.document;
    this.policies.set([]);
    this.error.set('');
    this.catalogLoaded.set(false);
    if (!document || !this.compatible || !this.issuingScopeSupported) {
      this.loading.set(false);
      return;
    }
    this.loading.set(true);
    try {
      const values = await firstValueFrom(this.http.get<unknown>(
        this.apis.core.manual.platformWorkflowExecution() + '/numbering-policies',
        { params: { workflowCode: document.workflowCode, scopeType: document.scopeTypeCode, scopeKey: document.scopeKey! } },
      ));
      if (generation !== this.generation || context !== this.catalogContext()) return;
      if (!Array.isArray(values) || !values.every(isPolicy) || new Set(values.map(policy => policy.id)).size !== values.length) {
        this.error.set('The published policy catalog could not be read. The saved selection is retained; refresh policies to try again.');
        return;
      }
      this.policies.set(values);
      this.catalogLoaded.set(true);
    } catch (error) {
      if (generation === this.generation && context === this.catalogContext())
        this.error.set(error instanceof HttpErrorResponse && [401, 403].includes(error.status)
          ? 'Access to numbering policies was refused. The saved selection is retained. Check your access before retrying.'
          : 'Numbering policies could not be loaded. The saved selection is retained; refresh policies to retry.');
    } finally {
      if (generation === this.generation) this.loading.set(false);
    }
  }
}

function isPolicy(value: unknown): value is Policy {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const policy = value as Record<string, unknown>;
  return typeof policy['id'] === 'string' && !!policy['id'] && typeof policy['code'] === 'string' &&
    !!policy['code'] && Number.isInteger(policy['revision']) && Number(policy['revision']) > 0 &&
    typeof policy['counterId'] === 'string' && !!policy['counterId'];
}

function numberingFormatSupported(document: WorkflowDefinitionDocument | null): boolean {
  return ['1.4', '1.5', '1.6', '1.7', '1.8', '1.9', '1.10', '1.11', '1.12',
    '1.13', '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(document?.payloadSchemaVersion ?? '');
}

/** Recognize only the existing format parts; unknown metadata never guesses code requirements. */
function readPolicyContext(parts: unknown): PolicyContext | null {
  if (!Array.isArray(parts) || parts.length < 1 || parts.length > 16) return null;
  const context: PolicyContext = { agency: null, program: null };
  let sequences = 0;
  for (const value of parts) {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
    const part = value as Record<string, unknown>;
    if (Object.keys(part).some(key => !['kind', 'value', 'width', 'maxLength'].includes(key))) return null;
    switch (part['kind']) {
      case 'literal':
        if (typeof part['value'] !== 'string' || !part['value']) return null;
        break;
      case 'sequence':
        if (part['value'] != null || !Number.isInteger(part['width']) || Number(part['width']) < 1 || Number(part['width']) > 19) return null;
        sequences++;
        break;
      case 'date':
        if (typeof part['value'] !== 'string' || !['yyyy', 'MM', 'dd'].includes(part['value'])) return null;
        break;
      case 'context': {
        const name = part['value'], limit = part['maxLength'];
        if (typeof name !== 'string' || !['agency', 'program', 'workflow'].includes(name) || !Number.isInteger(limit) || Number(limit) < 1 || Number(limit) > 32) return null;
        if (name === 'agency' || name === 'program') context[name] = Math.min(context[name] ?? 32, Number(limit));
        break;
      }
      default: return null;
    }
  }
  return sequences === 1 ? context : null;
}
